import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';

import i18n from 'i18n-js';
import Can from 'components/Can';
import Title from 'components/Title';
import { Table } from 'components/UI';
import Layout from 'components/Layout';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import { useHistory } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { SearchField } from 'components/FormFields';
import { useDispatch, useSelector } from 'react-redux';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import { DocumentsActions } from 'state/modules/documents';
import InputAdornment from '@mui/material/InputAdornment';
import { UpdateDocumentTypeModal } from '../../components/Modals';
import { buildUrl, truncateKeywordSpaces, toQueryString } from 'utils';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import { debounce } from 'lodash';
import { useQuery } from 'hooks';

const searchIconSx = {
  '& svg': {
    color: (theme) => theme.palette.secondary.light,
    width: 16,
  },
};

function DocumentTypesList() {
  const dispatch = useDispatch();
  const urlParams = useQuery();
  const history = useHistory();
  const { documentTypes, documentTypeSuccess, documentTypesLoading, documentTypesPagination } =
    useSelector((state) => state.documents);
  const [openCreateDocumentTypeModal, setOpenCreateDocumentTypeModal] = useState(false);
  const [params, setParams] = useState({ page: 1, limit: 10, ...urlParams });
  const [currentDocumentType, setCurrentDocumentType] = useState(null);
  const isMounted = useRef(false);
  const abortController = useRef(null);

  const refetch = documentTypeSuccess;

  const columns = useMemo(
    () => [
      {
        name: i18n.t('tableColumns.id'),
        selector: 'id',
        sortable: true,
        grow: 0,
      },
      {
        name: i18n.t('tableColumns.name'),
        selector: 'name',
        sortable: true,
        grow: 1,
      },
      {
        name: i18n.t('tableColumns.quantity'),
        selector: 'amount',
        cell: (row) => row?.amount || 0,
        sortable: true,
        grow: 1,
      },
      {
        name: i18n.t('common.actions'),
        selector: 'action',
        center: true,
        grow: 0,
        button: true,
        cell: (row) => {
          return (
            <Box display="flex" justifyContent="center">
              <Can I="update" on="document_type">
                <IconButton
                  aria-label="edit"
                  size="medium"
                  onClick={() => handleCreateModalOpen(row)}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>
              </Can>
              <IconButton
                aria-label="search"
                size="medium"
                onClick={() => history.push(buildUrl(`/documents?document_type_id=${row.id}`))}
              >
                <FindInPageIcon fontSize="inherit" />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [history]
  );

  /**
   * Get document type list
   * @param {object} params Request params
   */
  const fetchDocumentTypes = useCallback(
    (params = {}) => {
      const _params = { ...params };
      if (_params?.keyword) {
        _params.keyword = truncateKeywordSpaces(_params.keyword);
      }
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        DocumentsActions.fetchDocumentTypes(_params, { signal: abortController?.current?.signal })
      );
    },
    [dispatch]
  );

  const fetchDocumentTypesDebounced = useMemo(
    () => debounce(fetchDocumentTypes, 1000),
    [fetchDocumentTypes]
  );

  const handleCreateModalOpen = (current) => {
    if (current) {
      setCurrentDocumentType(current);
    }
    setOpenCreateDocumentTypeModal((prevState) => !prevState);
  };

  // Fetch on params change
  useEffect(() => {
    if (isMounted.current) {
      fetchDocumentTypesDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchDocumentTypesDebounced, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchDocumentTypes({ ...params });
    // eslint-disable-next-line
  }, []);

  // Fetch when data is outdated
  useEffect(() => {
    if (refetch) {
      fetchDocumentTypes(params);
    }
  }, [fetchDocumentTypes, params, refetch]);

  const handleParams = (newParams) => {
    setParams(newParams);
    const _params = toQueryString(newParams);
    history.replace(buildUrl(`/document-types${_params}`));
  };

  // search by status, keyword and organization
  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      [name]: value,
    };
    // remove empty fields
    Object.keys(filters).forEach((key) => !filters[key] && delete filters[key]);
    if (filters?.keyword) {
      filters.keyword = truncateKeywordSpaces(filters.keyword);
    }
    handleParams({ ...filters });
  };

  const handlePageChange = (page) => {
    const newParams = { ...params, page: page, limit: params.limit };
    handleParams(newParams);
  };

  // sort filter
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      page: documentTypesPagination?.currentPage,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  // change number of items per page
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  return (
    <Layout title={i18n.t('sidebar.document_types')}>
      <Container sx={{ minWidth: '100%', margin: 0 }}>
        <Grid container spacing={2}>
          <Grid item>
            <Title
              dataCy="document-types-title"
              size={{ sm: 8 }}
              title={i18n.t('sidebar.document_types')}
            />
          </Grid>
          <Can I="create" on="document_type">
            <Grid item offset={{ md: 'auto' }}>
              <Button
                onClick={handleCreateModalOpen}
                variant="contained"
                color="primary"
                data-cy="create-document-type-button"
                sx={{ textTransform: 'inherit' }}
              >
                {i18n.t('documents.create_document_type')}
              </Button>
            </Grid>
          </Can>
        </Grid>

        <Grid item xs={12} sx={{ paddingBottom: '25px' }}>
          <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-end"
            spacing={2}
          >
            <Grid item xs={12} sm={4} md={3}>
              <SearchField
                label={i18n.t('documents.search_document_type')}
                field={{
                  onChange: search,
                  name: 'keyword',
                  value: params.keyword,
                }}
                inputProps={{
                  startAdornment: (
                    <InputAdornment sx={searchIconSx} position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                data-cy="search-document-type"
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Paper>
            <Table
              data-cy="document-types-table"
              columns={columns}
              page={params.page}
              onSort={handleSort}
              data={documentTypes}
              limit={params.limit}
              loading={documentTypesLoading}
              handlePageChange={handlePageChange}
              handlePerRowsChange={handlePerRowsChange}
              totalRows={documentTypesPagination?.totalRows || 0}
            />
          </Paper>
        </Grid>
        {openCreateDocumentTypeModal && (
          <UpdateDocumentTypeModal
            open={true}
            id={currentDocumentType?.id}
            documentType={currentDocumentType}
            handleClick={handleCreateModalOpen}
            title={i18n.t('vehicle.change_vehicles_status')}
          />
        )}
      </Container>
    </Layout>
  );
}

export default DocumentTypesList;
